import { useEffect, useRef, useState } from "react";
import { Mail, Plus, Search } from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  Input,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { emailFieldError, isValidEmail } from "@/lib/emailValidator";
import { phoneFieldError, isValidPhone } from "@/lib/phoneValidator";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  externalRecipientsApi,
  type ExternalRecipientDto,
} from "@/api/collectionPackages";

// External Intake & Collection v1.0 PR 8 (Series 1) — find-or-create a
// recipient by email. The backend upsert endpoint dedupes on email, so
// the safe pattern is: 1) GET by-email; 2) if 404, surface a create
// form pre-filled with the searched email. One recipient per email.
//
// Account-First Phase 3 — `initialEmail` lets the composer auto-trigger
// a lookup when it arrives with `?email=` from PortalAccountDetailPage's
// "Send another request" / "Assign more groups" actions, so staff don't
// have to click "Look up" before the rest of the page hydrates.

interface Props {
  value: ExternalRecipientDto | null;
  onChange: (next: ExternalRecipientDto | null) => void;
  /** Phase 3 — seeds the email input and auto-runs the lookup once. */
  initialEmail?: string;
}

export function RecipientPicker({ value, onChange, initialEmail }: Props) {
  const toast = useToast();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [searching, setSearching] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createPhone, setCreatePhone] = useState("");
  const [creating, setCreating] = useState(false);
  const autoLookupDone = useRef(false);

  async function search() {
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSearching(true);
    setCreateMode(false);
    try {
      const found = await externalRecipientsApi.findByEmail(trimmed);
      onChange(found);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setCreateMode(true);
        setCreateName("");
        setCreatePhone("");
      } else {
        toast.error(extractErrorMessage(err));
      }
    } finally {
      setSearching(false);
    }
  }

  // Phase 3 — auto-run the lookup when seeded with an email, but only
  // once. Staff can still clear + search a different address freely.
  useEffect(() => {
    if (autoLookupDone.current) return;
    if (!initialEmail) return;
    if (value) return;
    autoLookupDone.current = true;
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail]);

  async function create() {
    const name = createName.trim();
    if (!name) return;
    const phone = createPhone.trim();
    if (phone && !isValidPhone(phone)) {
      toast.error("Enter a valid phone number.");
      return;
    }
    setCreating(true);
    try {
      const created = await externalRecipientsApi.create({
        fullName: name,
        email: email.trim(),
        phone: createPhone.trim() || null,
        linkedCounterpartyType: null,
        linkedCounterpartyId: null,
      });
      onChange(created);
      setCreateMode(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  if (value) {
    return (
      <div className="flex items-center justify-between gap-space-3 rounded-md border border-border-light p-space-3">
        <div className="flex items-center gap-space-2 min-w-0">
          <Mail size={14} className="text-gold-shadow shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="text-small font-semibold text-onyx truncate">{value.fullName}</div>
            <div className="text-caption text-gray-mid truncate">{value.email}</div>
          </div>
          {value.linkedCounterpartyType && (
            <Badge status="info">Linked · {value.linkedCounterpartyType}</Badge>
          )}
        </div>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => {
            onChange(null);
            setEmail("");
            setCreateMode(false);
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  const emailError = email ? emailFieldError(email, { required: false }) : undefined;
  const createPhoneError = createPhone
    ? phoneFieldError(createPhone, { required: false })
    : undefined;

  return (
    <div className="flex flex-col gap-space-3">
      <FormField
        label="Recipient email"
        htmlFor="recipient-email"
        helperText="We look up an existing recipient first; if none, you can create one."
        error={emailError}
      >
        <div className="flex gap-space-2">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid pointer-events-none"
              aria-hidden
            />
            <Input
              id="recipient-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setCreateMode(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void search();
                }
              }}
              placeholder="recipient@example.com"
              className="pl-space-5"
              invalid={!!emailError}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={() => void search()}
            disabled={email.trim().length === 0 || !!emailError || searching}
            loading={searching}
          >
            Look up
          </Button>
        </div>
      </FormField>

      {createMode && (
        <div className="rounded-md border border-gold-shadow/40 bg-paper-warm p-space-3 flex flex-col gap-space-3">
          <div className="flex items-center gap-space-2 text-small text-onyx">
            <Plus size={14} className="text-gold-shadow" aria-hidden />
            No recipient with <strong className="ml-space-1">{email.trim()}</strong> — create one?
          </div>
          <FormField label="Full name" htmlFor="recipient-name" required>
            <Input
              id="recipient-name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="Jane Buyer"
            />
          </FormField>
          <FormField label="Phone" htmlFor="recipient-phone" error={createPhoneError}>
            <Input
              id="recipient-phone"
              type="tel"
              value={createPhone}
              onChange={(e) => setCreatePhone(e.target.value)}
              placeholder="Optional"
              invalid={!!createPhoneError}
            />
          </FormField>
          <div className="flex items-center justify-end gap-space-2">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => setCreateMode(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void create()}
              disabled={createName.trim().length === 0 || !!createPhoneError || creating}
              loading={creating}
            >
              Create recipient
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
